import React from 'react';
import { createResponsiveImageConfig } from '../../utils/imageOptimizer';

/**
 * ResponsiveImage Component
 * A highly optimized image component with:
 * - WebP format with fallback
 * - Responsive srcset
 * - Lazy loading
 * - Art direction
 * - Blur-up loading effect
 * 
 * @param {Object} props - Component props
 * @param {string} props.src - Original image source
 * @param {string} props.size - Size identifier (thumbnail, small, medium, large)
 * @param {string} props.alt - Image alt text
 * @param {string} props.className - Custom class names
 * @param {string} props.sizes - Sizes attribute for responsive images
 * @param {Object} props.artDirection - Art direction config for different screen sizes
 * @param {number} props.width - Image width
 * @param {number} props.height - Image height
 * @param {string} props.loading - Loading strategy ('lazy', 'eager')
 * @param {Object} props.imgProps - Additional props for the img element
 */
const ResponsiveImage = ({
  src,
  size = 'medium',
  alt = '',
  className = '',
  sizes,
  artDirection,
  width,
  height,
  loading = 'lazy',
  imgProps = {},
  ...rest
}) => {
  // Create a complete responsive image configuration
  const imageConfig = createResponsiveImageConfig(src, size, {
    alt,
    className,
    sizes,
    artDirection,
    width,
    height,
    loading,
    ...rest
  });

  // If there's art direction config, render multiple sources
  if (imageConfig.artDirection) {
    return (
      <picture>
        {/* Art direction sources for WebP */}
        {imageConfig.artDirection.map((direction, index) => (
          <source
            key={`webp-direction-${index}`}
            media={direction.media}
            type="image/webp"
            srcSet={direction.webpSrcset}
            sizes={direction.sizes || sizes || imageConfig.sources[0].sizes}
          />
        ))}
        
        {/* Art direction sources for original format */}
        {imageConfig.artDirection.map((direction, index) => (
          <source
            key={`original-direction-${index}`}
            media={direction.media}
            type={`image/${direction.src.includes('.png') ? 'png' : 'jpeg'}`}
            srcSet={direction.srcset}
            sizes={direction.sizes || sizes || imageConfig.sources[1].sizes}
          />
        ))}
        
        {/* WebP source */}
        <source
          type="image/webp"
          srcSet={imageConfig.sources[0].srcset}
          sizes={imageConfig.sources[0].sizes}
        />
        
        {/* Original format source */}
        <source
          type={imageConfig.sources[1].type}
          srcSet={imageConfig.sources[1].srcset}
          sizes={imageConfig.sources[1].sizes}
        />
        
        {/* Fallback image */}
        <img
          src={imageConfig.img.src}
          alt={imageConfig.img.alt}
          loading={imageConfig.img.loading}
          className={imageConfig.img.className}
          width={imageConfig.img.width}
          height={imageConfig.img.height}
          {...imgProps}
        />
      </picture>
    );
  }

  // Standard responsive image without art direction
  return (
    <picture>
      {/* WebP source */}
      <source
        type="image/webp"
        srcSet={imageConfig.sources[0].srcset}
        sizes={imageConfig.sources[0].sizes}
      />
      
      {/* Original format source */}
      <source
        type={imageConfig.sources[1].type}
        srcSet={imageConfig.sources[1].srcset}
        sizes={imageConfig.sources[1].sizes}
      />
      
      {/* Fallback image */}
      <img
        src={imageConfig.img.src}
        alt={imageConfig.img.alt}
        loading={imageConfig.img.loading}
        className={imageConfig.img.className}
        width={imageConfig.img.width}
        height={imageConfig.img.height}
        {...imgProps}
      />
    </picture>
  );
};

export default ResponsiveImage; 